import Button from "@amzn/meridian/button";
import Column from "@amzn/meridian/column";
import Modal, { ModalFooter } from "@amzn/meridian/modal";
import Row from "@amzn/meridian/row";
import Tab, { TabGroup } from "@amzn/meridian/tab";
import Text from "@amzn/meridian/text";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import LoadingSpinner from "src/components/shared/loadingSpinner/loadingSpinner";
import { TOAST_TIMEOUT } from "src/constants/constants";
import { useAppDispatch } from "src/store/store";
import { createToast } from "src/store/toastsSlice";
import { getCurrentRegion } from "src/utils/regionUtils";
import { isRegionCode } from "./adminReportUtils";
import DocumentDescription from "./components/documentDescription";
import DocumentTestCase from "./components/documentTestCase";
import {
  ChangeChip,
  CoverageChip,
  DocumentTypeChip,
  NodeStatusChip,
  RegionChip,
  ScopeChip,
} from "./components/documentChips";
import LoadError from "./components/loadError";
import ReportPageHeader from "./components/reportPageHeader";
import TestCaseModal from "./components/testCaseModal";
import adminReportService from "./service/adminReportService";
import { PolicyDocument, RegionCode } from "./types";

type DetailTab = "description" | "testCase";

const resolveRegion = (regionParam: string | null): RegionCode => {
  if (regionParam && isRegionCode(regionParam)) return regionParam;
  const current = getCurrentRegion();
  return isRegionCode(current) ? current : "na";
};

const DocumentDetail = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { attachmentId } = useParams();
  const [searchParams] = useSearchParams();
  const region = resolveRegion(searchParams.get("region"));

  const [document, setDocument] = useState<PolicyDocument>();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [activeTab, setActiveTab] = useState<DetailTab>("description");
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isConfirmingRemove, setIsConfirmingRemove] = useState(false);

  const documentsUrl = `/admin/documents?region=${region}`;

  const loadDocument = async () => {
    // Without this an absent route param would request /documents/undefined.
    if (!attachmentId) return;
    setIsLoading(true);
    setHasError(false);
    try {
      setDocument(
        (await adminReportService.getDocument(region, attachmentId)) ??
          undefined,
      );
    } catch {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDocument();
  }, [attachmentId, region]);

  const confirmRemove = async () => {
    if (!document) return;
    setIsRemoving(true);
    try {
      // Identity comes from the loaded record, not the URL: both are guaranteed
      // strings, and region is the realm the row was actually read from.
      setDocument(
        await adminReportService.deleteTestCase(
          document.region,
          document.attachmentId,
        ),
      );
      setIsConfirmingRemove(false);
    } catch {
      dispatch(
        createToast({
          type: "error",
          message: "Failed to remove the test case",
          timeout: TOAST_TIMEOUT,
        }),
      );
    } finally {
      setIsRemoving(false);
    }
  };

  if (!attachmentId) {
    return (
      <div className="admin-report">
        <ReportPageHeader
          crumbs={[
            { label: "← Documents", onClick: () => navigate(documentsUrl) },
            { label: "Not found" },
          ]}
        />
        <div className="admin-report__body">
          <Text>No document specified.</Text>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="admin-report">
        <ReportPageHeader
          crumbs={[
            { label: "← Documents", onClick: () => navigate(documentsUrl) },
          ]}
        />
        <LoadingSpinner />
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="admin-report">
        <ReportPageHeader
          crumbs={[
            { label: "← Documents", onClick: () => navigate(documentsUrl) },
          ]}
        />
        <div className="admin-report__body">
          <LoadError subject="this document" onRetry={loadDocument} />
        </div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="admin-report">
        <ReportPageHeader
          crumbs={[
            { label: "← Documents", onClick: () => navigate(documentsUrl) },
            { label: "Not found" },
          ]}
        />
        <div className="admin-report__body">
          <Text>{`No document found for attachment ${attachmentId}.`}</Text>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-report">
      <ReportPageHeader
        crumbs={[
          { label: "← Documents", onClick: () => navigate(documentsUrl) },
          { label: document.fileName },
        ]}
      />

      <div className="admin-report__detail-title">
        <span className="admin-report__detail-name">{document.fileName}</span>
        <RegionChip region={region} />
        <ScopeChip region={region} isGlobal={document.isGlobal} />
        <DocumentTypeChip documentType={document.nodeDocumentType} />
        <NodeStatusChip status={document.nodeStatus} />
        <ChangeChip changeType={document.changeType} />
        <CoverageChip testCaseId={document.testCase?.testCaseId} />
      </div>

      <div className="admin-report__detail-labels">
        <span className="admin-report__doc-sub">{document.nodeTitle}</span>
      </div>

      <div className="admin-report__detail-tabs">
        <TabGroup value={activeTab} onChange={setActiveTab} fill="line">
          <Tab value="description">Description</Tab>
          <Tab value="testCase">
            {document.testCase ? "Test Case (1)" : "Test Case (0)"}
          </Tab>
        </TabGroup>
      </div>

      <div className="admin-report__detail-body">
        {activeTab === "description" ? (
          <DocumentDescription document={document} />
        ) : (
          <section className="admin-report__card">
            <DocumentTestCase
              document={document}
              onAdd={() => setIsEditorOpen(true)}
              onEdit={() => setIsEditorOpen(true)}
              onRemove={() => setIsConfirmingRemove(true)}
            />
          </section>
        )}
      </div>

      <TestCaseModal
        open={isEditorOpen}
        onClose={() => setIsEditorOpen(false)}
        region={document.region}
        attachmentId={document.attachmentId}
        expectedFileName={document.fileName}
        currentQuestion={document.testCase?.question}
        onSaved={loadDocument}
      />

      <Modal
        open={isConfirmingRemove}
        onClose={() => setIsConfirmingRemove(false)}
        title="Remove Test Case"
        width="520px"
        bodySpacingInset="400"
      >
        <Column spacing="300">
          <Text>{`Remove the test case from ${document.fileName}?`}</Text>
          <Text type="b100" color="secondary">
            The document returns to the uncovered list, so nothing will check
            that it can be retrieved until a new question is added.
          </Text>
        </Column>
        <ModalFooter>
          <Row alignmentHorizontal="end" widths="fit">
            <Button
              type="secondary"
              size="small"
              onClick={() => setIsConfirmingRemove(false)}
            >
              Cancel
            </Button>
            <Button size="small" disabled={isRemoving} onClick={confirmRemove}>
              Confirm
            </Button>
          </Row>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default DocumentDetail;
